import { useMemo, useReducer } from "react";

import type { WidgetDataSlice } from "#app/widgets/dashboards/widget-drilldown";

/**
 * Which overlay the dashboard has open, and how the detail dialog was asked to
 * open. One reducer, because the pieces move together: a drilldown sets the
 * slice AND the target AND bumps the Data request in one act, and closing the
 * dialog clears the target and the slice together, never one without the other.
 */
export type DashboardOverlaysState = {
  /** The Add-Widget gallery (or, on a managed surface, the Manage panel) is open. */
  addOpen: boolean;
  /** Widget id pending replacement — the Add-Widget gallery doubles as the
   *  Replace picker while this is set. */
  replaceId: string | null;
  /** The detail dialog's target, held as an ID and re-derived from the live
   *  list so in-dialog edits (rename, config) reflect immediately instead of
   *  showing a stale snapshot; the index drives the dialog's prev/next stepping. */
  maximizedId: string | null;
  /** A clicked chart element's slice — set alongside `maximizedId` by the
   *  drilldown channel, cleared on close/nav so it never leaks across widgets. */
  dataSlice: WidgetDataSlice | null;
  /** Bumped on every BOARD drilldown open so the detail dialog shows its Data
   *  panel alone — even for an unfiltered open (the metric widget) where
   *  `dataSlice` stays null. The plain Fullscreen affordance leaves this
   *  untouched, so it opens the widget beside Data. A chart click inside the
   *  open dialog never reaches this counter: the dialog wraps its body in its
   *  own drilldown provider that swaps the slice and keeps the user's layout. */
  dataRequestId: number;
  /** Bumped when the gear is pressed so the detail dialog opens on its
   *  Settings tab — the gear opens the same fullscreen editor as Maximize,
   *  pre-drilled to settings. Plain Fullscreen leaves this untouched, so it
   *  opens on Data. */
  settingsRequestId: number;
  /** Bumped when a widget body asks for the chart-only presentation — the body
   *  alone fills the dialog. Plain Fullscreen leaves this untouched. */
  chartRequestId: number;
};

type Action =
  | { type: "add"; open: boolean }
  | { type: "replace"; id: string | null }
  | { type: "maximize"; id: string | null }
  | { type: "step"; id: string }
  | { type: "openData"; id: string; slice: WidgetDataSlice | null }
  | { type: "openChart"; id: string }
  | { type: "openSettings"; id: string }
  | { type: "slice"; slice: WidgetDataSlice | null };

const INITIAL: DashboardOverlaysState = {
  addOpen: false,
  replaceId: null,
  maximizedId: null,
  dataSlice: null,
  dataRequestId: 0,
  settingsRequestId: 0,
  chartRequestId: 0,
};

function reduce(state: DashboardOverlaysState, action: Action): DashboardOverlaysState {
  switch (action.type) {
    case "add":
      return { ...state, addOpen: action.open };
    case "replace":
      return { ...state, replaceId: action.id };
    case "maximize":
      // Closing clears the slice with the target; opening plain keeps it.
      return action.id === null
        ? { ...state, maximizedId: null, dataSlice: null }
        : { ...state, maximizedId: action.id };
    case "step":
      return { ...state, maximizedId: action.id, dataSlice: null };
    case "openData":
      return {
        ...state,
        dataSlice: action.slice,
        dataRequestId: state.dataRequestId + 1,
        maximizedId: action.id,
      };
    case "openChart":
      return {
        ...state,
        dataSlice: null,
        chartRequestId: state.chartRequestId + 1,
        maximizedId: action.id,
      };
    case "openSettings":
      return {
        ...state,
        settingsRequestId: state.settingsRequestId + 1,
        maximizedId: action.id,
      };
    case "slice":
      return { ...state, dataSlice: action.slice };
  }
}

export function useDashboardOverlays(): DashboardOverlaysState & {
  setAddOpen: (open: boolean) => void;
  setReplaceId: (id: string | null) => void;
  /** Opens the detail dialog on a widget, or closes it (`null`). */
  setMaximizedId: (id: string | null) => void;
  /** Moves the open dialog to a sibling widget; the slice never travels. */
  stepMaximized: (id: string) => void;
  openData: (id: string, slice: WidgetDataSlice | null) => void;
  openChart: (id: string) => void;
  openSettings: (id: string) => void;
  setDataSlice: (slice: WidgetDataSlice | null) => void;
} {
  const [state, dispatch] = useReducer(reduce, INITIAL);
  const actions = useMemo(
    () => ({
      setAddOpen: (open: boolean) => dispatch({ type: "add", open }),
      setReplaceId: (id: string | null) => dispatch({ type: "replace", id }),
      setMaximizedId: (id: string | null) => dispatch({ type: "maximize", id }),
      stepMaximized: (id: string) => dispatch({ type: "step", id }),
      openData: (id: string, slice: WidgetDataSlice | null) =>
        dispatch({ type: "openData", id, slice }),
      openChart: (id: string) => dispatch({ type: "openChart", id }),
      openSettings: (id: string) => dispatch({ type: "openSettings", id }),
      setDataSlice: (slice: WidgetDataSlice | null) => dispatch({ type: "slice", slice }),
    }),
    [],
  );
  return { ...state, ...actions };
}
